import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  CheckCircle, 
  XCircle, 
  PencilSimple,
  SpinnerGap,
  Warning,
  Clock,
  VideoCamera,
  ArrowRight,
  Eye
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";
const EMPTY_STATE = "https://static.prod-images.emergentagent.com/jobs/9ce68c36-1b03-4868-a37f-38db73ced2af/images/ac1e39a43e42bf5a6e74d582c502572ddd5e50b14cd865b9eb668efafb6c961d.png";

const TYPE_STYLES = {
  qa: "badge-qa",
  procedure: "badge-procedure",
  script: "badge-script",
  sales_script: "badge-script",
  "sales-script": "badge-script",
  objection: "badge-objection",
  rebuttal: "badge-rebuttal",
  concept: "badge-concept",
};

// Vincent #421 (Eric 2026-07-06 21h50 SPEC STRICK) — Libellé humain FR.
// Le type brut de la DB ("sales-script", "sales_script", "script") doit
// TOUJOURS s'afficher "script de vente" avec une couleur distincte.
// Tâche 1324, vague 2 — les deux langues sur la même ligne (auparavant deux
// tables jumelles : un type ajouté d'un côté manquait de l'autre).
const TYPE_LABELS = {
  qa: ["Q&R", "Q&A"],
  procedure: ["Procédure", "Procedure"],
  script: ["Script de vente", "Sales script"],
  sales_script: ["Script de vente", "Sales script"],
  "sales-script": ["Script de vente", "Sales script"],
  objection: ["Objection", "Objection"],
  rebuttal: ["Réfutation", "Rebuttal"],
  concept: ["Concept", "Concept"],
};
// Tâche 1324, vague 2 (Eric 2026-06-30) — Libellés des champs d'une unité de
// connaissance, en UN seul exemplaire. Le mode édition et le mode lecture les
// écrivaient chacun de leur côté : traduire deux listes jumelles, c'est
// accepter qu'elles divergent au premier champ ajouté.
const FIELD_LABELS = {
  question: ["Question", "Question"],
  answer: ["Réponse", "Answer"],
  scenario: ["Scénario", "Scenario"],
  response_v1: ["Réponse (v1)", "Response (v1)"],
  response_v2: ["Réponse améliorée (v2)", "Improved response (v2)"],
  response_v3: ["Réponse validée (v3)", "Validated response (v3)"],
  phrase_evitement: ["Phrase d'évitement", "Avoidance phrase"],
  objection: ["Objection", "Objection"],
  body: ["Contenu", "Content"],
  key_takeaways: ["Points clés à retenir", "Key takeaways"],
  definition: ["Définition", "Definition"],
  definition_concept: ["Définition (concept)", "Definition (concept)"],
  key_points: ["Points-clés", "Key points"],
  key_points_concept: ["Points-clés (concept)", "Key points (concept)"],
  example: ["Exemple", "Example"],
  objective: ["Objectif de la procédure", "Procedure objective"],
  prerequisites: ["Prérequis", "Prerequisites"],
  steps: ["Étapes de la procédure", "Procedure steps"],
  summary: ["Résumé", "Summary"],
  statement: ["Énoncé", "Statement"],
  justification: ["Justification", "Justification"],
  objection_evitee: ["Objection évitée", "Objection avoided"],
  theme: ["Thème", "Theme"],
  keywords: ["Mots-clés", "Keywords"],
  theme_category: ["Catégorie", "Category"],
  theme_subcategory: ["Sous-catégorie", "Subcategory"],
  textual_proof: ["Citation textuelle", "Verbatim quote"],
  textual_proof_verbatim: ["Citation textuelle (verbatim)", "Verbatim quote"],
};
const fieldLabel = (cle, lang) => (FIELD_LABELS[cle] || [cle, cle])[lang === "en" ? 1 : 0];

// Vincent #421 — Couleur distincte pour "script de vente" (violet)
// afin de le différencier des autres types.
const TYPE_INLINE_STYLE = {
  script: { backgroundColor: "#F5EBFF", color: "#6B21A8", borderColor: "#D8B4FE" },
  sales_script: { backgroundColor: "#F5EBFF", color: "#6B21A8", borderColor: "#D8B4FE" },
  "sales-script": { backgroundColor: "#F5EBFF", color: "#6B21A8", borderColor: "#D8B4FE" },
};
const typeLabel = (t, lang) => (TYPE_LABELS[t] || [t, t])[lang === "en" ? 1 : 0];

export default function ReviewQueuePage() {
  const { user } = useAuth();
  const { lang, t, tr } = useLang();
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingUnits, setPendingUnits] = useState([]);
  const [flags, setFlags] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  // Vincent #430/#431 (Eric 2026-07-07 SPEC STRICK) — Feedback INSTANTANÉ
  // : dès qu'on clique Approuver/Rejeter, on affiche "Acceptée" en vert /
  // "Refusée" en rouge à côté du bouton, avant même le retour serveur.
  // La carte disparaît 500 ms plus tard.
  const [lastDecision, setLastDecision] = useState(null); // { unitId, action }
  // B727 (Eric 2026-02-12) — Scripts legacy à nettoyer / régénérer.
  const [legacyScripts, setLegacyScripts] = useState(null); // {total, test_orphans, regenerable, videos_to_regenerate}
  const [legacyCleanupLoading, setLegacyCleanupLoading] = useState(false);
  const [legacyRegenModal, setLegacyRegenModal] = useState(null); // videos_to_regenerate list after cleanup

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [queueRes, videosRes, legacyRes] = await Promise.all([
        axios.get(`${API}/api/admin/review-queue`, { withCredentials: true }),
        axios.get(`${API}/api/videos`, { withCredentials: true }),
        axios.get(`${API}/api/admin/review-queue/legacy-script-kus`, { withCredentials: true }).catch(() => null),
      ]);

      setPendingUnits(queueRes.data.pending_units || []);
      setFlags(queueRes.data.flags || []);
      setVideos(videosRes.data.items || videosRes.data);
      // B727 — Détecter les scripts legacy (format v0/v1 sans V1/V2/V3).
      if (legacyRes?.data && legacyRes.data.total > 0) {
        setLegacyScripts(legacyRes.data);
      } else {
        setLegacyScripts(null);
      }
    } catch (err) {
      console.error("Failed to load review queue:", err);
    } finally {
      setLoading(false);
    }
  };

  // B727 (Eric 2026-02-12) — Purge les scripts legacy et affiche la liste
  // des vidéos à régénérer via le nouveau pipeline (V1/V2/V3).
  const handleLegacyCleanup = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? `Nettoyer les ${legacyScripts?.total || 0} script(s) legacy ? Les artefacts de test seront purgés silencieusement. Les scripts pertinents seront rejetés — tu pourras les régénérer via le nouveau pipeline sur chaque vidéo source.`
        : `Clean up the ${legacyScripts?.total || 0} legacy script(s)? Test artifacts will be purged silently. Real scripts will be rejected — you can regenerate them via the new pipeline on each source video.`,
      { confirmLabel: lang === "fr" ? "Nettoyer" : "Clean up" },
    );
    if (!ok) return;
    setLegacyCleanupLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/review-queue/cleanup-legacy-script-kus`,
        {},
        { withCredentials: true },
      );
      toast.success(data.message || (lang === "fr" ? "Nettoyage terminé" : "Cleanup done"));
      setLegacyScripts(null);
      // Ouvre le modal Régénération si des vidéos concernées existent.
      if ((data.videos_to_regenerate || []).length > 0) {
        setLegacyRegenModal(data.videos_to_regenerate);
      }
      // Recharge la file pour refléter la disparition des KUs.
      await loadData();
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    } finally {
      setLegacyCleanupLoading(false);
    }
  };

  const getVideoTitle = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    return video?.title || "Unknown Video";
  };

  const handleAction = async (unitId, action) => {
    // Vincent #430/#431 — feedback instantané avant l'appel serveur
    if (action === "approve" || action === "reject") {
      setLastDecision({ unitId, action });
    }
    setActionLoading(true);
    try {
      const payload = { action, notes: reviewNotes };
      
      if (action === "edit" && editContent) {
        try {
          payload.edited_content = JSON.parse(editContent);
        } catch (parseErr) {
          setActionLoading(false);
          setLastDecision(null);
          toast.error(tr("Le contenu JSON est invalide. Vérifiez la syntaxe.", "The JSON content is invalid. Check the syntax."));
          return;
        }
      }
      
      await axios.put(
        `${API}/api/knowledge/${unitId}/review`,
        payload,
        { withCredentials: true }
      );
      // Attendre 500 ms que Vincent voit "Acceptée"/"Refusée" avant retrait
      await new Promise((r) => setTimeout(r, 500));
      setPendingUnits(prev => prev.filter(u => u.id !== unitId));
      setSelectedUnit(null);
      setReviewNotes("");
      setIsEditing(false);
      setEditContent("");
      setLastDecision(null);
    } catch (err) {
      console.error("Failed to process review:", err);
      setLastDecision(null);
    } finally {
      setActionLoading(false);
    }
  };

  const openUnit = (unit) => {
    setSelectedUnit(unit);
    setIsEditing(false);
    setEditContent(JSON.stringify(unit.content, null, 2));
    setReviewNotes("");
  };

  const handleBulkApprove = async () => {
    const ok = await confirmDialog(`Approuver les ${pendingUnits.length} unités en attente ?`);
    if (!ok) return;
    setBulkLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/bulk-approve`,
        {},
        { withCredentials: true }
      );
      setPendingUnits([]);
      toast.error(data.message);
    } catch (err) {
      console.error("Bulk approve failed:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  const formatTimestamp = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{t("review_title")}</h1>
          <p className="text-zinc-500 mt-1">
            {t("review_subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingUnits.length > 0 && (
            <Button
              onClick={handleBulkApprove}
              disabled={bulkLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
              data-testid="bulk-approve-btn"
            >
              {bulkLoading ? (
                <SpinnerGap className="animate-spin mr-2" size={16} />
              ) : (
                <CheckCircle className="mr-2" size={16} />
              )}
              {lang === "en" ? `Approve all (${pendingUnits.length})` : `Tout approuver (${pendingUnits.length})`}
            </Button>
          )}
          <span className="text-sm text-zinc-500">
            {tr(`${pendingUnits.length} en attente • ${flags.length} signalées`, `${pendingUnits.length} pending • ${flags.length} flagged`)}
          </span>
        </div>
      </div>

      {/* B727 (Eric 2026-02-12) — Bannière scripts legacy détectés */}
      {legacyScripts && legacyScripts.total > 0 && (
        <div
          className="mb-4 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-3"
          data-testid="legacy-scripts-banner"
        >
          <Warning size={22} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">
              {lang === "fr"
                ? `${legacyScripts.total} script(s) au format legacy détecté(s)`
                : `${legacyScripts.total} legacy-format script(s) detected`}
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              {lang === "fr"
                ? `Ces scripts proviennent de l'ancien pipeline (avant mai 2026, format sans V1/V2/V3). Ils n'apparaissent PAS dans l'onglet « Scripts de vente » des fiches vidéo. ${legacyScripts.regenerable.length} sont rattachés à ${legacyScripts.videos_to_regenerate.length} vidéo(s) réelle(s) — tu pourras les régénérer proprement. ${legacyScripts.test_orphans.length > 0 ? `${legacyScripts.test_orphans.length} sont des artefacts de test (video source disparue) — purgés silencieusement.` : ""}`
                : `These scripts come from the legacy pipeline (pre-May 2026, no V1/V2/V3). They do NOT appear in the video's "Sales scripts" tab. ${legacyScripts.regenerable.length} are attached to ${legacyScripts.videos_to_regenerate.length} real video(s) — you can regenerate them cleanly. ${legacyScripts.test_orphans.length > 0 ? `${legacyScripts.test_orphans.length} are test artifacts (video source gone) — purged silently.` : ""}`}
            </p>
          </div>
          <Button
            onClick={handleLegacyCleanup}
            disabled={legacyCleanupLoading}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs shrink-0"
            data-testid="legacy-scripts-cleanup-btn"
          >
            {legacyCleanupLoading ? (
              <SpinnerGap className="animate-spin mr-1.5" size={14} />
            ) : null}
            {lang === "fr" ? "Nettoyer & voir vidéos à régénérer" : "Clean up & see videos to regenerate"}
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-zinc-100 p-1 rounded-sm">
          <TabsTrigger
            value="pending"
            className="px-4 py-2 text-sm rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-pending"
          >
            {tr("Révision en attente", "Pending review")} ({pendingUnits.length})
          </TabsTrigger>
          <TabsTrigger
            value="flags"
            className="px-4 py-2 text-sm rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-flags"
          >
            {tr("Problèmes signalés", "Flagged issues")} ({flags.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Content */}
      {activeTab === "pending" ? (
        pendingUnits.length > 0 ? (
          <div className="space-y-4">
            {pendingUnits.map((unit) => (
              <ReviewCard
                key={unit.id}
                unit={unit}
                videoTitle={getVideoTitle(unit.video_id)}
                formatTimestamp={formatTimestamp}
                onSelect={() => openUnit(unit)}
                selected={selectedUnit?.id === unit.id}
                lang={lang}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            message={tr("Aucune révision en attente", "No pending review")}
            sublabel={tr("Toutes les unités de connaissances ont été révisées", "Every knowledge unit has been reviewed")}
          />
        )
      ) : (
        flags.length > 0 ? (
          <div className="space-y-4">
            {flags.map((flag) => (
              <FlagCard key={flag.id} flag={flag} />
            ))}
          </div>
        ) : (
          <EmptyState
            message={tr("Aucun problème signalé", "No issue flagged")}
            sublabel={tr("Aucun doublon ni contradiction détecté", "No duplicate or contradiction detected")}
          />
        )
      )}

      {/* Review panel */}
      {selectedUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedUnit(null)} />
          <div className="relative bg-white rounded-sm shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-sm border ${TYPE_STYLES[selectedUnit.type] || ""}`}
                      style={TYPE_INLINE_STYLE[selectedUnit.type] || {}}
                      data-testid={`review-badge-${selectedUnit.type}`}
                    >
                      {typeLabel(selectedUnit.type, lang)}
                    </span>
                    {selectedUnit.confidence && (
                      <span className="text-xs text-zinc-400">
                        {Math.round(selectedUnit.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg font-semibold font-['Outfit']">{selectedUnit.title || (lang === "fr" ? "Unité de connaissance à réviser" : "Review Knowledge Unit")}</h2>
                </div>
                <button
                  onClick={() => setSelectedUnit(null)}
                  className="text-zinc-400 hover:text-zinc-600"
                  data-testid="close-review-panel"
                >
                  <XCircle size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {isEditing ? (
                /* Edit mode — Vincent 2026-05-06 (bug #66, #67): champs séparés + repli JSON avancé */
                <div className="space-y-4">
                  {(() => {
                    let parsed = {};
                    try { parsed = JSON.parse(editContent || "{}"); } catch { parsed = {}; }
                    const updateField = (key, val) => {
                      const next = { ...parsed, [key]: val };
                      setEditContent(JSON.stringify(next, null, 2));
                    };
                    const fieldList = [
                      { key: "question", label: fieldLabel("question", lang), rows: 3 },
                      { key: "answer", label: fieldLabel("answer", lang), rows: 5 },
                      // Vincent #66 (concepts) — UI graphique au lieu de JSON
                      { key: "definition", label: fieldLabel("definition_concept", lang), rows: 4 },
                      { key: "example", label: fieldLabel("example", lang), rows: 3 },
                      // Vincent #67 (procedures) — UI graphique au lieu de JSON
                      { key: "objective", label: fieldLabel("objective", lang), rows: 2 },
                      { key: "summary", label: fieldLabel("summary", lang), rows: 3 },
                      { key: "statement", label: fieldLabel("statement", lang), rows: 3 },
                      { key: "justification", label: fieldLabel("justification", lang), rows: 3 },
                      { key: "objection_evitee", label: fieldLabel("objection_evitee", lang), rows: 2 },
                      { key: "theme", label: fieldLabel("theme", lang), rows: 1 },
                      { key: "keywords", label: fieldLabel("keywords", lang), rows: 1 },
                      { key: "theme_category", label: fieldLabel("theme_category", lang), rows: 1 },
                      { key: "theme_subcategory", label: fieldLabel("theme_subcategory", lang), rows: 1 },
                      { key: "textual_proof", label: fieldLabel("textual_proof_verbatim", lang), rows: 4 },
                    ];
                    return (
                      <div className="space-y-3">
                        {fieldList.map(({ key, label, rows }) =>
                          parsed[key] != null ? (
                            <div key={key}>
                              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 block mb-1">{label}</label>
                              <textarea
                                value={parsed[key] || ""}
                                onChange={(e) => updateField(key, e.target.value)}
                                rows={rows}
                                className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
                                data-testid={`edit-field-${key}`}
                              />
                            </div>
                          ) : null
                        )}
                        {/* Procédure — array of steps */}
                        {Array.isArray(parsed.steps) && (
                          <div data-testid="edit-array-steps">
                            <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 block mb-1">{fieldLabel("steps", lang)}</label>
                            {parsed.steps.map((step, idx) => (
                              <div key={idx} className="flex gap-2 mb-1.5">
                                <span className="text-xs text-zinc-500 mt-1.5 font-mono">{idx + 1}.</span>
                                <textarea
                                  value={typeof step === "string" ? step : JSON.stringify(step)}
                                  onChange={(e) => {
                                    const newSteps = [...parsed.steps];
                                    newSteps[idx] = e.target.value;
                                    updateField("steps", newSteps);
                                  }}
                                  rows={2}
                                  className="flex-1 px-2 py-1 border border-zinc-200 rounded-sm text-sm"
                                  data-testid={`edit-step-${idx}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => updateField("steps", parsed.steps.filter((_, i) => i !== idx))}
                                  className="text-red-500 hover:bg-red-50 px-1.5 rounded"
                                >✗</button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => updateField("steps", [...parsed.steps, ""])}
                              className="text-xs text-teal-700 hover:bg-teal-50 px-2 py-0.5 rounded mt-1"
                            >{tr("+ ajouter étape", "+ add step")}</button>
                          </div>
                        )}
                        {/* Vincent #66 — Concept key_points (UI graphique au lieu de JSON) */}
                        {Array.isArray(parsed.key_points) && (
                          <div data-testid="edit-array-key_points">
                            <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 block mb-1">{fieldLabel("key_points_concept", lang)}</label>
                            {parsed.key_points.map((pt, idx) => (
                              <div key={idx} className="flex gap-2 mb-1.5">
                                <span className="text-xs text-zinc-500 mt-1.5 font-mono">•</span>
                                <textarea
                                  value={typeof pt === "string" ? pt : JSON.stringify(pt)}
                                  onChange={(e) => {
                                    const arr = [...parsed.key_points];
                                    arr[idx] = e.target.value;
                                    updateField("key_points", arr);
                                  }}
                                  rows={1}
                                  className="flex-1 px-2 py-1 border border-zinc-200 rounded-sm text-sm"
                                  data-testid={`edit-keypoint-${idx}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => updateField("key_points", parsed.key_points.filter((_, i) => i !== idx))}
                                  className="text-red-500 hover:bg-red-50 px-1.5 rounded"
                                >✗</button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => updateField("key_points", [...parsed.key_points, ""])}
                              className="text-xs text-teal-700 hover:bg-teal-50 px-2 py-0.5 rounded mt-1"
                            >{tr("+ ajouter point-clé", "+ add key point")}</button>
                          </div>
                        )}
                        {/* Vincent #67 — Procédure prerequisites (UI graphique) */}
                        {Array.isArray(parsed.prerequisites) && (
                          <div data-testid="edit-array-prerequisites">
                            <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 block mb-1">{fieldLabel("prerequisites", lang)}</label>
                            {parsed.prerequisites.map((pr, idx) => (
                              <div key={idx} className="flex gap-2 mb-1.5">
                                <span className="text-xs text-zinc-500 mt-1.5 font-mono">▸</span>
                                <textarea
                                  value={typeof pr === "string" ? pr : JSON.stringify(pr)}
                                  onChange={(e) => {
                                    const arr = [...parsed.prerequisites];
                                    arr[idx] = e.target.value;
                                    updateField("prerequisites", arr);
                                  }}
                                  rows={1}
                                  className="flex-1 px-2 py-1 border border-zinc-200 rounded-sm text-sm"
                                  data-testid={`edit-prereq-${idx}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => updateField("prerequisites", parsed.prerequisites.filter((_, i) => i !== idx))}
                                  className="text-red-500 hover:bg-red-50 px-1.5 rounded"
                                >✗</button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => updateField("prerequisites", [...parsed.prerequisites, ""])}
                              className="text-xs text-teal-700 hover:bg-teal-50 px-2 py-0.5 rounded mt-1"
                            >{tr("+ ajouter prérequis", "+ add prerequisite")}</button>
                          </div>
                        )}
                        <details className="border border-zinc-200 rounded-sm p-2">
                          <summary className="text-xs font-semibold text-zinc-500 cursor-pointer">{tr("Mode JSON avancé (lecture seule expert)", "Advanced JSON mode (expert read-only)")}</summary>
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={10}
                            className="w-full mt-2 px-3 py-2 border border-zinc-200 rounded-sm text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
                            data-testid="edit-content-textarea"
                          />
                        </details>
                      </div>
                    );
                  })()}
                  <div>
                    <label className="text-sm font-medium block mb-2">{tr("Notes de révision", "Review notes")}</label>
                    <Textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder={tr("Raison de la modification...", "Reason for the change...")}
                      rows={2}
                      className="rounded-sm"
                      data-testid="edit-review-notes"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => handleAction(selectedUnit.id, "edit")}
                      disabled={actionLoading}
                      className="flex-1 h-11 bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm"
                      data-testid="save-edit-btn"
                    >
                      {actionLoading ? (
                        <SpinnerGap className="animate-spin mr-2" size={18} />
                      ) : (
                        <CheckCircle className="mr-2" size={18} />
                      )}
                      {tr("Sauvegarder et approuver", "Save and approve")}
                    </Button>
                    <Button
                      onClick={() => setIsEditing(false)}
                      variant="outline"
                      className="h-11 rounded-sm"
                      data-testid="cancel-edit-btn"
                    >
                      {t("cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                /* View mode — Vincent 2026-05-06 + 2026-05-07 (bugs #66 + #67):
                   UI graphique structuree pour TOUS les types (Q&R, concepts,
                   procedures, regles). Le fallback JSON brut a ete supprime au
                   profit d'une boucle generique sur les cles inconnues. */
                <>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-6 space-y-3">
                    <h4 className="text-sm font-medium mb-2">{tr("Contenu", "Content")}</h4>
                    {(() => {
                      const c = selectedUnit.content || {};
                      const L = (cle) => fieldLabel(cle, lang);
                      const knownFields = [
                        [L("question"), c.question],
                        [L("answer"), c.answer],
                        // Vincent B722 (Eric 2026-02-11) — Scripts de vente :
                        // afficher scénario + réponse (v1/v2/v3) comme dans
                        // l'onglet Scripts de vente de la Base de connaissances.
                        [L("scenario"), c.scenario],
                        [L("response_v1"), c.response || c.response_v1],
                        [L("response_v2"), c.response_v2],
                        [L("response_v3"), c.response_v3],
                        [L("phrase_evitement"), c.phrase_evitement],
                        [L("objection"), c.objection],
                        // Vincent B722 — Synthèses : body + points clés
                        [L("body"), c.body],
                        [L("key_takeaways"), c.key_takeaways],
                        // Vincent #66 (concepts) — definition + key_points
                        [L("definition"), c.definition],
                        [L("key_points"), c.key_points],
                        [L("example"), c.example],
                        // Vincent #67 (procedures) — steps + objective + prerequisites
                        [L("objective"), c.objective || c.procedure_objective],
                        [L("prerequisites"), c.prerequisites],
                        [L("steps"), c.steps || c.procedure],
                        [L("summary"), c.summary],
                        [L("statement"), c.statement],
                        [L("justification"), c.justification],
                        [L("objection_evitee"), c.objection_evitee],
                        [L("theme"), c.theme],
                        [L("keywords"), c.keywords],
                        [L("theme_category"), c.theme_category],
                        [L("theme_subcategory"), c.theme_subcategory],
                        [L("textual_proof"), c.textual_proof],
                      ];
                      const knownKeys = new Set([
                        "question", "answer", "definition", "key_points", "example",
                        "objective", "procedure_objective", "prerequisites", "steps", "procedure",
                        "summary", "statement", "justification", "objection_evitee",
                        "theme", "keywords", "theme_category", "theme_subcategory", "textual_proof",
                        // Vincent B722 — Scripts + Synthèses
                        "scenario", "response", "response_v1", "response_v2", "response_v3",
                        "phrase_evitement", "objection", "body", "key_takeaways",
                      ]);
                      const renderedFields = knownFields.filter(([_, v]) => v != null && v !== "" && (!Array.isArray(v) || v.length > 0));
                      // Vincent #66 — capture les clés inconnues pour les afficher proprement
                      // au lieu de retomber sur un JSON.stringify global.
                      const unknownEntries = Object.entries(c).filter(([k, v]) =>
                        !knownKeys.has(k) && v != null && v !== "" && (!Array.isArray(v) || v.length > 0)
                      );

                      if (renderedFields.length === 0 && unknownEntries.length === 0) {
                        return <div className="text-sm italic text-zinc-500">Contenu vide.</div>;
                      }

                      const renderValue = (val) => {
                        if (Array.isArray(val)) {
                          return (
                            <ol className="list-decimal pl-5 space-y-1">
                              {val.map((item, i) => (
                                <li key={i}>
                                  {typeof item === "string"
                                    ? item
                                    : (item && typeof item === "object")
                                      ? Object.entries(item).map(([ik, iv]) => (
                                          <div key={ik}><span className="font-semibold capitalize">{ik}: </span>{String(iv)}</div>
                                        ))
                                      : String(item)}
                                </li>
                              ))}
                            </ol>
                          );
                        }
                        if (val && typeof val === "object") {
                          return (
                            <div className="space-y-1">
                              {Object.entries(val).map(([k, v]) => (
                                <div key={k}>
                                  <span className="text-xs font-semibold uppercase text-zinc-400">{k}: </span>
                                  <span>{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return String(val);
                      };

                      return (
                        <>
                          {renderedFields.map(([label, val]) => (
                            <div key={label} className="border-l-2 border-teal-300 pl-3" data-testid={`review-field-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{label}</div>
                              <div className="text-sm text-zinc-800 mt-0.5 whitespace-pre-wrap break-words">
                                {renderValue(val)}
                              </div>
                            </div>
                          ))}
                          {unknownEntries.length > 0 && (
                            <details className="mt-2">
                              <summary className="text-xs font-semibold text-zinc-400 cursor-pointer hover:text-zinc-600">
                                ⚙ Champs additionnels ({unknownEntries.length})
                              </summary>
                              <div className="mt-2 space-y-2 pl-3">
                                {unknownEntries.map(([k, v]) => (
                                  <div key={k} className="border-l-2 border-zinc-200 pl-3">
                                    <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">{k}</div>
                                    <div className="text-sm text-zinc-700 mt-0.5 whitespace-pre-wrap break-words">{renderValue(v)}</div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-zinc-500 mb-6">
                    <span className="flex items-center gap-1">
                      <VideoCamera size={16} />
                      {getVideoTitle(selectedUnit.video_id)}
                    </span>
                    {selectedUnit.timestamps?.start && (
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        {formatTimestamp(selectedUnit.timestamps.start)} - {formatTimestamp(selectedUnit.timestamps.end)}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-2">{tr("Notes de révision (optionnel)", "Review notes (optional)")}</label>
                      <Textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder={tr("Ajoutez des notes...", "Add notes...")}
                        rows={3}
                        className="rounded-sm"
                        data-testid="review-notes-input"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => handleAction(selectedUnit.id, "approve")}
                        disabled={actionLoading}
                        className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                        data-testid="approve-btn"
                      >
                        {actionLoading ? (
                          <SpinnerGap className="animate-spin mr-2" size={18} />
                        ) : (
                          <CheckCircle className="mr-2" size={18} />
                        )}
                        {t("approve")}
                      </Button>
                      {/* Vincent #430 — badge instantané "Acceptée" en vert */}
                      {lastDecision?.unitId === selectedUnit.id && lastDecision?.action === "approve" && (
                        <span className="px-3 py-1.5 bg-teal-600 text-white text-sm font-bold rounded-md animate-fade-in" data-testid="approve-instant-badge">{tr("Acceptée ✓", "Accepted ✓")}</span>
                      )}
                      <Button
                        onClick={() => setIsEditing(true)}
                        variant="outline"
                        className="flex-1 h-11 border-[#002FA7] text-[#002FA7] hover:bg-blue-50 rounded-sm"
                        data-testid="edit-btn"
                      >
                        <PencilSimple className="mr-2" size={18} />
                        {t("edit")}
                      </Button>
                      <Button
                        onClick={() => handleAction(selectedUnit.id, "reject")}
                        disabled={actionLoading}
                        variant="outline"
                        className="flex-1 h-11 border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                        data-testid="reject-btn"
                      >
                        <XCircle className="mr-2" size={18} />
                        {t("reject")}
                      </Button>
                      {/* Vincent #431 — badge instantané "Refusée" en rouge */}
                      {lastDecision?.unitId === selectedUnit.id && lastDecision?.action === "reject" && (
                        <span className="px-3 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md animate-fade-in" data-testid="reject-instant-badge">{tr("Refusée ✗", "Rejected ✗")}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* B727 (Eric 2026-02-12) — Modal Régénération vidéos après cleanup */}
      {legacyRegenModal && legacyRegenModal.length > 0 && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setLegacyRegenModal(null)}
          data-testid="legacy-regen-modal"
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                  <ArrowRight size={20} weight="bold" className="text-[#002FA7]" />
                  {lang === "fr" ? "Régénérer les scripts sur ces vidéos" : "Regenerate scripts on these videos"}
                </h3>
                <p className="text-xs text-zinc-600 mt-1">
                  {lang === "fr"
                    ? "Les scripts legacy ont été nettoyés. Ouvre chaque vidéo puis clique « Générer les scripts » dans l'onglet Scripts. Le nouveau pipeline produira des scripts riches (V1/V2/V3 + tableau de validation + objections)."
                    : "Legacy scripts have been cleaned up. Open each video then click 'Generate scripts' in the Scripts tab. The new pipeline will produce rich scripts (V1/V2/V3 + validation table + objections)."}
                </p>
              </div>
              <button
                onClick={() => setLegacyRegenModal(null)}
                className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500 hover:text-zinc-800 transition-colors shrink-0"
                data-testid="legacy-regen-close-btn"
                aria-label={lang === "fr" ? "Fermer" : "Close"}
              >
                <XCircle size={22} />
              </button>
            </div>
            <ul className="space-y-2" data-testid="legacy-regen-list">
              {legacyRegenModal.map((v) => (
                <li
                  key={v.video_id}
                  className="border border-zinc-200 rounded-lg p-3 flex items-start justify-between gap-3 hover:border-[#002FA7] transition-colors"
                  data-testid={`legacy-regen-video-${v.video_id.slice(0, 8)}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <VideoCamera size={16} className="text-[#002FA7] shrink-0" />
                      <p className="font-semibold text-sm text-zinc-900 truncate">{v.video_title}</p>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      {lang === "fr"
                        ? `${v.kus_purged} script(s) legacy à régénérer`
                        : `${v.kus_purged} legacy script(s) to regenerate`}
                    </p>
                  </div>
                  <a
                    href={`/dashboard/videos/${v.video_id}?tab=scripts`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#002FA7] hover:bg-[#001F7A] text-white text-xs font-semibold shrink-0 no-underline"
                    data-testid={`legacy-regen-open-${v.video_id.slice(0, 8)}`}
                  >
                    {lang === "fr" ? "Ouvrir & régénérer" : "Open & regenerate"}
                    <ArrowRight size={12} weight="bold" />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-zinc-100 text-right">
              <Button
                variant="outline"
                onClick={() => setLegacyRegenModal(null)}
                data-testid="legacy-regen-dismiss-btn"
              >
                {lang === "fr" ? "Plus tard" : "Later"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewCard({ unit, videoTitle, formatTimestamp, onSelect, selected, lang }) {
  return (
    <div
      className={`bg-white border rounded-sm p-5 cursor-pointer transition-all ${
        selected ? "border-[#002FA7] ring-2 ring-[#002FA7]/20" : "border-zinc-200 hover:border-zinc-300"
      }`}
      onClick={onSelect}
      data-testid={`review-card-${unit.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-sm border ${TYPE_STYLES[unit.type] || ""}`}
              style={TYPE_INLINE_STYLE[unit.type] || {}}
              data-testid={`review-card-badge-${unit.type}`}
            >
              {typeLabel(unit.type, lang)}
            </span>
            {unit.confidence && (
              <span className={`text-xs ${unit.confidence < 0.5 ? 'text-yellow-600' : 'text-zinc-400'}`}>
                {Math.round(unit.confidence * 100)}% confidence
              </span>
            )}
          </div>
          <h3 className="font-medium text-sm">{unit.title || (lang === "fr" ? "Sans titre" : "Untitled")}</h3>
          <p className="text-sm text-zinc-500 mt-1 line-clamp-2">
            {(() => {
              // Vincent 2026-05-06 (bug #66, #67): aperçu lisible (Q/R) au lieu du JSON brut.
              // Vincent B722 (Eric 2026-02-11) — Ajout scripts + synthèses.
              const c = unit.content || {};
              const preview = c.question
                || c.statement
                || c.scenario                 // scripts
                || c.response || c.response_v3 || c.response_v2 || c.response_v1
                || c.body                     // synthèses
                || c.definition               // concepts
                || c.steps?.[0]
                || c.answer
                || "";
              const stripped = String(preview)
                .replace(/\*\*(.+?)\*\*/g, "$1")
                .replace(/\*(.+?)\*/g, "$1")
                .replace(/^#{1,6}\s+/gm, "")
                .replace(/\n{2,}/g, " · ")
                .replace(/\n/g, " ");
              return stripped ? stripped.slice(0, 200) : (lang === "fr" ? "(contenu vide)" : "(empty)");
            })()}
          </p>
        </div>
        <Button variant="outline" size="sm" className="ml-4 gap-1" data-testid={`review-btn-${unit.id}`}>
          <Eye size={16} />
          {lang === "fr" ? "Modifier" : "Review"}
        </Button>
      </div>
      
      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400">
        <span className="flex items-center gap-1">
          <VideoCamera size={14} />
          {videoTitle}
        </span>
        {unit.timestamps?.start && (
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {formatTimestamp(unit.timestamps.start)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock size={14} />
          {new Date(unit.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

function FlagCard({ flag }) {
  return (
    <div className="bg-white border border-yellow-200 rounded-sm p-5">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
          <Warning size={18} className="text-yellow-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-sm">
            {flag.type === "potential_duplicate" ? "Potential Duplicate" : flag.type}
          </h3>
          <p className="text-sm text-zinc-500 mt-1">
            Similarity score: {Math.round((flag.similarity || 0) * 100)}%
          </p>
          <p className="text-xs text-zinc-400 mt-2">
            Created: {new Date(flag.created_at).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message, sublabel }) {
  return (
    <div className="py-16 flex flex-col items-center justify-center text-center">
      <img src={EMPTY_STATE} alt="Empty" className="w-40 h-40 mb-4 opacity-60" />
      <h3 className="font-medium text-zinc-900">{message}</h3>
      <p className="text-sm text-zinc-500 mt-1">{sublabel}</p>
    </div>
  );
}
